import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronRight, Sparkles, ThumbsUp, ThumbsDown, FileText, Database, Zap, Plus, Link, ArrowRight, Check, Loader2, Save } from 'lucide-react';
import { Card, CardHeader, CardBody } from '../ui/Card';
import Button from '../ui/Button';
import { toast } from 'sonner';
import { api } from '../../lib/api';

interface ColumnMapping {
  columnName: string;
  suggestedMapping?: {
    nodeLabel: string;
    propertyName: string;
    confidence: number;
    reasoning: string;
  };
  acceptedMapping?: {
    nodeLabel: string;
    propertyName: string;
    confidence: number;
    isAccepted: boolean;
    userModified: boolean;
    reasoning?: string;
    suggestedMapping?: {
      nodeLabel: string;
      propertyName: string;
      confidence: number;
      reasoning: string;
    };
  };
  include: boolean;
  dataType: string;
  sampleValues: string[];
  dataQuality: {
    nullPercentage: number;
    uniqueness: number;
    issues?: string[];
  };
}

interface FileClassification {
  dataCategory: string;
  businessEntity: string;
  sourceArea: string;
  relatedProcesses: string[];
  importance: string;
  reasoning: string;
}

interface SuggestedNode {
  nodeLabel: string;
  category: string;
  description: string;
  reasoning: string;
  priority: string;
  suggestedProperties: string[];
  sampleDataColumns: string[];
}

interface SuggestedRelationship {
  fromNode: string;
  relationshipType: string;
  toNode: string;
  description: string;
  reasoning: string;
  properties: string[];
}

interface SuggestedProperty {
  nodeLabel: string;
  propertyName: string;
  propertyType: string;
  reasoning: string;
  sourceColumn: string;
}

interface SchemaEnhancements {
  schemaAlignment: string;
  missingCoveragePercentage: number;
  suggestedNodes: SuggestedNode[];
  suggestedRelationships: SuggestedRelationship[];
  suggestedProperties: SuggestedProperty[];
  enhancementSummary: string;
}

interface FileAnalysis {
  fileName: string;
  sourceSystem: string;
  detectedFileType: string;
  totalRows: number;
  totalColumns: number;
  fileClassification?: FileClassification;
  schemaEnhancements?: SchemaEnhancements;
  columns: ColumnMapping[];
  overallConfidence: number;
  recommendations: string[];
  warnings: string[];
}

interface FileAnalysisCardProps {
  caseId: string;
  fileId: string;
  analysis: FileAnalysis;
  schema: {
    nodes: Array<{ label: string; properties: any[] }>;
    relationships: Array<{ type: string; properties: any[] }>;
  };
  onAcceptMapping: (fileName: string, mappings: ColumnMapping[]) => void;
  onRejectMapping: (fileName: string) => void;
  onUpdateMapping: (fileName: string, mappings: ColumnMapping[]) => void;
  onSchemaRefresh?: () => void;
}

export const FileAnalysisCard: React.FC<FileAnalysisCardProps> = ({
  caseId,
  fileId,
  analysis,
  schema,
  onAcceptMapping,
  onRejectMapping,
  onUpdateMapping,
  onSchemaRefresh
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  // Initialize with acceptedMapping if available, otherwise use suggestedMapping
  const [editedMappings, setEditedMappings] = useState<ColumnMapping[]>(
    analysis.columns.map(col => ({
      ...col,
      // If acceptedMapping exists, make it the active mapping by copying to suggestedMapping
      suggestedMapping: col.acceptedMapping ? {
        nodeLabel: col.acceptedMapping.suggestedMapping?.nodeLabel || '',
        propertyName: col.acceptedMapping.suggestedMapping?.propertyName || '',
        confidence: col.acceptedMapping.suggestedMapping?.confidence || 0.5,
        reasoning: col.acceptedMapping.suggestedMapping?.reasoning || 'User accepted mapping'
      } : col.suggestedMapping
    }))
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Update editedMappings when analysis changes (e.g., user navigates back to this file)
  React.useEffect(() => {
    setEditedMappings(
      analysis.columns.map(col => ({
        ...col,
        // If acceptedMapping exists, make it the active mapping by copying to suggestedMapping
        suggestedMapping: col.acceptedMapping ? {
          nodeLabel: col.acceptedMapping.suggestedMapping?.nodeLabel || '',
          propertyName: col.acceptedMapping.suggestedMapping?.propertyName || '',
          confidence: col.acceptedMapping.suggestedMapping?.confidence || 0.5,
          reasoning: col.acceptedMapping.suggestedMapping?.reasoning || 'User accepted mapping'
        } : col.suggestedMapping
      }))
    );
  }, [analysis.columns]);

  // Track loading state for each suggestion
  const [acceptingNode, setAcceptingNode] = useState<string | null>(null);
  const [acceptingRelationship, setAcceptingRelationship] = useState<string | null>(null);
  const [acceptingProperty, setAcceptingProperty] = useState<string | null>(null);

  // Check if node exists in schema
  const isNodeInSchema = (nodeLabel: string): boolean => {
    return schema.nodes.some(n => n.label === nodeLabel);
  };

  // Check if relationship exists in schema
  const isRelationshipInSchema = (fromNode: string, relType: string, toNode: string): boolean => {
    return schema.relationships.some(
      r => r.type === relType &&
      (r as any).from === fromNode &&
      (r as any).to === toNode
    );
  };

  // Check if property exists on node
  const isPropertyOnNode = (nodeLabel: string, propertyName: string): boolean => {
    const node = schema.nodes.find(n => n.label === nodeLabel);
    if (!node || !node.properties) return false;

    if (Array.isArray(node.properties)) {
      return node.properties.some((prop: any) => {
        if (typeof prop === 'string') return prop === propertyName;
        if (typeof prop === 'object' && prop.name) return prop.name === propertyName;
        return false;
      });
    }
    return false;
  };

  const handleToggleColumn = (index: number) => {
    const updated = [...editedMappings];
    updated[index].include = !updated[index].include;
    setEditedMappings(updated);
    onUpdateMapping(analysis.fileName, updated);
  };

  const handleMappingChange = (index: number, nodeLabel: string, propertyName: string, shouldNotify: boolean = false) => {
    const updated = [...editedMappings];
    if (!updated[index].suggestedMapping) {
      updated[index].suggestedMapping = {
        nodeLabel,
        propertyName,
        confidence: 0.5,
        reasoning: 'Manual mapping'
      };
    } else {
      updated[index].suggestedMapping.nodeLabel = nodeLabel;
      updated[index].suggestedMapping.propertyName = propertyName;
    }
    setEditedMappings(updated);

    // Only notify parent when user finishes editing (blur event) or selects node
    if (shouldNotify) {
      onUpdateMapping(analysis.fileName, updated);
    }
  };

  // Get available properties for selected node
  const getNodeProperties = (nodeLabel: string): string[] => {
    const node = schema.nodes.find(n => n.label === nodeLabel);
    if (!node || !node.properties) return [];

    // Handle different property formats
    if (Array.isArray(node.properties)) {
      return node.properties.map((prop: any) => {
        if (typeof prop === 'string') return prop;
        if (typeof prop === 'object' && prop.name) return prop.name;
        return '';
      }).filter(Boolean);
    }

    return [];
  };

  // Accept suggested node
  const handleAcceptNode = async (node: SuggestedNode) => {
    const nodeKey = node.nodeLabel;

    // Check if already exists in schema
    if (isNodeInSchema(nodeKey)) {
      toast.info(`Node "${node.nodeLabel}" already exists in schema`);
      return;
    }

    setAcceptingNode(nodeKey);

    try {
      // Convert to backend format
      const nodeDefinition = {
        label: node.nodeLabel,
        category: node.category,
        description: node.description,
        properties: node.suggestedProperties.map(propName => ({
          name: propName,
          type: 'String', // Default type
          required: false,
          unique: false,
          indexed: false,
          description: `${propName} property`
        }))
      };

      await api.post(`/schema-design/cases/${caseId}/schema/accept-node`, nodeDefinition);

      toast.success(`Node "${node.nodeLabel}" added to schema`);

      // Refresh schema to update dropdown options and button states
      if (onSchemaRefresh) {
        onSchemaRefresh();
      }
    } catch (error: any) {
      console.error('Failed to accept node:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to accept node';
      toast.error(`Failed to add node: ${errorMessage}`);
    } finally {
      setAcceptingNode(null);
    }
  };

  // Accept suggested relationship
  const handleAcceptRelationship = async (relationship: SuggestedRelationship) => {
    const relKey = `${relationship.fromNode}-${relationship.relationshipType}-${relationship.toNode}`;

    // Check if already exists in schema
    if (isRelationshipInSchema(relationship.fromNode, relationship.relationshipType, relationship.toNode)) {
      toast.info(`Relationship already exists in schema`);
      return;
    }

    setAcceptingRelationship(relKey);

    try {
      // Convert to backend format
      const relationshipDefinition = {
        type: relationship.relationshipType,
        from: relationship.fromNode,  // Backend expects 'from', not 'from_node'
        to: relationship.toNode,      // Backend expects 'to', not 'to_node'
        description: relationship.description,
        properties: relationship.properties.map(propName => ({
          name: propName,
          type: 'String',
          required: false,
          description: `${propName} property`
        }))
      };

      await api.post(`/schema-design/cases/${caseId}/schema/accept-relationship`, relationshipDefinition);

      toast.success(`Relationship "${relationship.relationshipType}" added to schema`);

      // Refresh schema
      if (onSchemaRefresh) {
        onSchemaRefresh();
      }
    } catch (error: any) {
      console.error('Failed to accept relationship:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to accept relationship';
      toast.error(`Failed to add relationship: ${errorMessage}`);
    } finally {
      setAcceptingRelationship(null);
    }
  };

  // Accept suggested property
  const handleAcceptProperty = async (property: SuggestedProperty) => {
    const propKey = `${property.nodeLabel}-${property.propertyName}`;

    // Check if already exists on the node
    if (isPropertyOnNode(property.nodeLabel, property.propertyName)) {
      toast.info(`Property "${property.propertyName}" already exists on ${property.nodeLabel}`);
      return;
    }

    setAcceptingProperty(propKey);

    try {
      // Convert to backend format
      const propertyDefinition = {
        name: property.propertyName,
        type: property.propertyType,
        required: false,
        unique: false,
        indexed: false,
        description: property.reasoning
      };

      await api.post(`/schema-design/cases/${caseId}/schema/accept-property`, {
        node_label: property.nodeLabel,
        property: propertyDefinition
      });

      toast.success(`Property "${property.propertyName}" added to ${property.nodeLabel}`);

      // Refresh schema
      if (onSchemaRefresh) {
        onSchemaRefresh();
      }
    } catch (error: any) {
      console.error('Failed to accept property:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to accept property';
      toast.error(`Failed to add property: ${errorMessage}`);
    } finally {
      setAcceptingProperty(null);
    }
  };

  // Save mappings to backend without accepting/closing the card
  const handleSaveMappings = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'}/data-files/files/${fileId}/analysis/accept`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            caseId: caseId,
            fileName: analysis.fileName,
            columnMappings: editedMappings,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save mappings');
      }

      toast.success('Mappings saved successfully');
      console.log('✅ Saved mappings for', analysis.fileName);
    } catch (error: any) {
      console.error('Failed to save mappings:', error);
      toast.error(`Failed to save mappings: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600 bg-green-50';
    if (confidence >= 0.5) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  const getAlignmentColor = (alignment: string) => {
    switch (alignment.toLowerCase()) {
      case 'full': return 'bg-green-100 text-green-800 border-green-300';
      case 'partial': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'minimal': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'none': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-neutral-100 text-neutral-800 border-neutral-300';
    }
  };

  const getImportanceColor = (importance: string) => {
    switch (importance.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'low': return 'bg-neutral-100 text-neutral-800 border-neutral-300';
      default: return 'bg-neutral-100 text-neutral-800 border-neutral-300';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'low': return 'bg-neutral-100 text-neutral-800 border-neutral-300';
      default: return 'bg-neutral-100 text-neutral-800 border-neutral-300';
    }
  };

  const includedColumns = editedMappings.filter(c => c.include).length;
  const mappedColumns = editedMappings.filter(c => c.include && c.suggestedMapping).length;

  return (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 hover:bg-neutral-100 rounded"
            >
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-neutral-600" />
              ) : (
                <ChevronRight className="w-5 h-5 text-neutral-600" />
              )}
            </button>

            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-neutral-900">{analysis.fileName}</h3>
                <span className={`text-xs px-2 py-1 rounded-full ${getConfidenceColor(analysis.overallConfidence)}`}>
                  {Math.round(analysis.overallConfidence * 100)}% confidence
                </span>
              </div>
              <div className="flex items-center gap-4 mt-1 text-xs text-neutral-600">
                <span>Source: <span className="font-medium">{analysis.sourceSystem || 'Unknown'}</span></span>
                <span>Type: <span className="font-medium">{analysis.detectedFileType || 'Unknown'}</span></span>
                {analysis.totalRows != null && <span>{analysis.totalRows.toLocaleString()} rows</span>}
                <span>{includedColumns}/{analysis.totalColumns ?? 0} columns included</span>
                <span>{mappedColumns} mapped</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveMappings}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRejectMapping(analysis.fileName)}
            >
              <ThumbsDown className="w-4 h-4" />
              Reject
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => onAcceptMapping(analysis.fileName, editedMappings)}
            >
              <ThumbsUp className="w-4 h-4" />
              Accept Mapping
            </Button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardBody>
          {/* Recommendations */}
          {analysis.recommendations.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-blue-900 mb-1">Recommendations</div>
                  <ul className="text-sm text-blue-800 space-y-1">
                    {analysis.recommendations.map((rec, idx) => (
                      <li key={idx}>• {rec}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Warnings */}
          {analysis.warnings.length > 0 && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-amber-900 mb-1">Warnings</div>
                  <ul className="text-sm text-amber-800 space-y-1">
                    {analysis.warnings.map((warn, idx) => (
                      <li key={idx}>• {warn}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* File Classification */}
          {analysis.fileClassification && (
            <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
              <div className="flex items-start gap-3 mb-3">
                <FileText className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-neutral-900 mb-1">File Classification</h4>
                  <p className="text-xs text-neutral-600">What this data represents in your business context</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <div className="text-xs text-neutral-600 mb-1">Data Category</div>
                  <div className="text-sm font-medium text-neutral-900">{analysis.fileClassification.dataCategory}</div>
                </div>
                <div>
                  <div className="text-xs text-neutral-600 mb-1">Business Entity</div>
                  <div className="text-sm font-medium text-neutral-900">{analysis.fileClassification.businessEntity}</div>
                </div>
                <div>
                  <div className="text-xs text-neutral-600 mb-1">Source Area</div>
                  <div className="text-sm font-medium text-neutral-900">{analysis.fileClassification.sourceArea}</div>
                </div>
                <div>
                  <div className="text-xs text-neutral-600 mb-1">Importance</div>
                  <span className={`inline-block text-xs px-2 py-1 rounded border ${getImportanceColor(analysis.fileClassification.importance)}`}>
                    {analysis.fileClassification.importance.toUpperCase()}
                  </span>
                </div>
              </div>

              {analysis.fileClassification.relatedProcesses.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs text-neutral-600 mb-1">Related Business Processes</div>
                  <div className="flex flex-wrap gap-1">
                    {analysis.fileClassification.relatedProcesses.map((process, idx) => (
                      <span key={idx} className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                        {process}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-purple-200">
                <div className="text-xs text-neutral-600 mb-1">Reasoning</div>
                <p className="text-sm text-neutral-700 italic">{analysis.fileClassification.reasoning}</p>
              </div>
            </div>
          )}

          {/* Schema Enhancement Suggestions */}
          {analysis.schemaEnhancements && (
            <div className="mb-4">
              <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg mb-3">
                <div className="flex items-start gap-3 mb-3">
                  <Zap className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-neutral-900 mb-1">Schema Enhancement Analysis</h4>
                    <p className="text-xs text-neutral-600">Assessment of how well your current schema fits this data</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <div className="text-xs text-neutral-600 mb-1">Schema Alignment</div>
                    <span className={`inline-block text-xs px-2 py-1 rounded border font-medium ${getAlignmentColor(analysis.schemaEnhancements.schemaAlignment)}`}>
                      {analysis.schemaEnhancements.schemaAlignment.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-600 mb-1">Missing Coverage</div>
                    <div className="text-sm font-medium text-neutral-900">
                      {analysis.schemaEnhancements.missingCoveragePercentage.toFixed(0)}% of data not covered
                    </div>
                  </div>
                </div>

                <div className="pt-3 border-t border-indigo-200">
                  <div className="text-xs text-neutral-600 mb-1">Enhancement Summary</div>
                  <p className="text-sm text-neutral-700">{analysis.schemaEnhancements.enhancementSummary}</p>
                </div>
              </div>

              {/* Suggested Nodes */}
              {analysis.schemaEnhancements.suggestedNodes.length > 0 && (
                <div className="mb-3 p-4 bg-white border border-green-200 rounded-lg">
                  <div className="flex items-start gap-2 mb-3">
                    <Plus className="w-4 h-4 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <h5 className="text-sm font-semibold text-neutral-900">
                        Suggested New Nodes ({analysis.schemaEnhancements.suggestedNodes.length})
                      </h5>
                      <p className="text-xs text-neutral-600">New entities to add to your schema</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {analysis.schemaEnhancements.suggestedNodes.map((node, idx) => {
                      const nodeKey = node.nodeLabel;
                      const isAccepted = isNodeInSchema(nodeKey);
                      const isAccepting = acceptingNode === nodeKey;

                      return (
                        <div key={idx} className="p-3 bg-green-50 border border-green-200 rounded">
                          <div className="flex items-start gap-2 mb-2">
                            <Database className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-neutral-900">{node.nodeLabel}</span>
                                <span className="text-xs px-2 py-0.5 bg-neutral-100 text-neutral-700 rounded">
                                  {node.category}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded border ${getPriorityColor(node.priority)}`}>
                                  {node.priority}
                                </span>
                                {isAccepted && (
                                  <span className="text-xs px-2 py-0.5 bg-green-600 text-white rounded flex items-center gap-1">
                                    <Check className="w-3 h-3" />
                                    Accepted
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-neutral-700 mb-2">{node.description}</p>
                              <div className="text-xs text-neutral-600 mb-2">
                                <span className="font-medium">Why needed:</span> {node.reasoning}
                              </div>

                              {node.suggestedProperties.length > 0 && (
                                <div className="text-xs">
                                  <span className="font-medium text-neutral-700">Properties:</span>{' '}
                                  <span className="text-neutral-600">{node.suggestedProperties.join(', ')}</span>
                                </div>
                              )}

                              {node.sampleDataColumns.length > 0 && (
                                <div className="text-xs mt-1">
                                  <span className="font-medium text-neutral-700">Source columns:</span>{' '}
                                  <span className="text-neutral-600">{node.sampleDataColumns.join(', ')}</span>
                                </div>
                              )}
                            </div>

                            {/* Accept button */}
                            <div className="ml-auto">
                              <button
                                onClick={() => handleAcceptNode(node)}
                                disabled={isAccepting || isAccepted}
                                className={`
                                  px-3 py-1 text-xs rounded flex items-center gap-1
                                  ${isAccepted
                                    ? 'bg-green-100 text-green-700 cursor-not-allowed'
                                    : 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50'
                                  }
                                `}
                              >
                                {isAccepting ? (
                                  <>
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Accepting...
                                  </>
                                ) : isAccepted ? (
                                  <>
                                    <Check className="w-3 h-3" />
                                    Accepted
                                  </>
                                ) : (
                                  <>
                                    <Plus className="w-3 h-3" />
                                    Accept
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Suggested Relationships */}
              {analysis.schemaEnhancements.suggestedRelationships.length > 0 && (
                <div className="mb-3 p-4 bg-white border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2 mb-3">
                    <Link className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <h5 className="text-sm font-semibold text-neutral-900">
                        Suggested New Relationships ({analysis.schemaEnhancements.suggestedRelationships.length})
                      </h5>
                      <p className="text-xs text-neutral-600">New connections between entities</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {analysis.schemaEnhancements.suggestedRelationships.map((rel, idx) => {
                      const relKey = `${rel.fromNode}-${rel.relationshipType}-${rel.toNode}`;
                      const isAccepted = isRelationshipInSchema(rel.fromNode, rel.relationshipType, rel.toNode);
                      const isAccepting = acceptingRelationship === relKey;

                      return (
                        <div key={idx} className="p-3 bg-blue-50 border border-blue-200 rounded">
                          <div className="flex items-start gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-medium text-blue-900">{rel.fromNode}</span>
                                <ArrowRight className="w-3 h-3 text-blue-600" />
                                <span className="text-xs px-2 py-0.5 bg-blue-200 text-blue-800 rounded font-mono">
                                  {rel.relationshipType}
                                </span>
                                <ArrowRight className="w-3 h-3 text-blue-600" />
                                <span className="text-sm font-medium text-blue-900">{rel.toNode}</span>
                                {isAccepted && (
                                  <span className="text-xs px-2 py-0.5 bg-blue-600 text-white rounded flex items-center gap-1 ml-2">
                                    <Check className="w-3 h-3" />
                                    Accepted
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-neutral-700 mb-1">{rel.description}</p>
                              <div className="text-xs text-neutral-600">
                                <span className="font-medium">Why needed:</span> {rel.reasoning}
                              </div>
                              {rel.properties.length > 0 && (
                                <div className="text-xs text-neutral-600 mt-1">
                                  <span className="font-medium">Properties:</span> {rel.properties.join(', ')}
                                </div>
                              )}
                            </div>

                            {/* Accept button */}
                            <div className="ml-auto">
                              <button
                                onClick={() => handleAcceptRelationship(rel)}
                                disabled={isAccepting || isAccepted}
                                className={`
                                  px-3 py-1 text-xs rounded flex items-center gap-1
                                  ${isAccepted
                                    ? 'bg-blue-100 text-blue-700 cursor-not-allowed'
                                    : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'
                                  }
                                `}
                              >
                                {isAccepting ? (
                                  <>
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Accepting...
                                  </>
                                ) : isAccepted ? (
                                  <>
                                    <Check className="w-3 h-3" />
                                    Accepted
                                  </>
                                ) : (
                                  <>
                                    <Link className="w-3 h-3" />
                                    Accept
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Suggested Property Enhancements */}
              {analysis.schemaEnhancements.suggestedProperties.length > 0 && (
                <div className="mb-3 p-4 bg-white border border-purple-200 rounded-lg">
                  <div className="flex items-start gap-2 mb-3">
                    <Plus className="w-4 h-4 text-purple-600 mt-0.5" />
                    <div className="flex-1">
                      <h5 className="text-sm font-semibold text-neutral-900">
                        Suggested Property Enhancements ({analysis.schemaEnhancements.suggestedProperties.length})
                      </h5>
                      <p className="text-xs text-neutral-600">New properties for existing nodes</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {analysis.schemaEnhancements.suggestedProperties.map((prop, idx) => {
                      const propKey = `${prop.nodeLabel}-${prop.propertyName}`;
                      const isAccepted = isPropertyOnNode(prop.nodeLabel, prop.propertyName);
                      const isAccepting = acceptingProperty === propKey;

                      return (
                        <div key={idx} className="p-2 bg-purple-50 border border-purple-200 rounded text-xs">
                          <div className="flex items-start gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-neutral-900">{prop.nodeLabel}</span>
                                <span className="text-neutral-600">•</span>
                                <span className="font-mono text-purple-700">{prop.propertyName}</span>
                                <span className="text-xs px-1 py-0.5 bg-neutral-100 text-neutral-700 rounded">
                                  {prop.propertyType}
                                </span>
                                {isAccepted && (
                                  <span className="text-xs px-2 py-0.5 bg-purple-600 text-white rounded flex items-center gap-1 ml-2">
                                    <Check className="w-3 h-3" />
                                    Accepted
                                  </span>
                                )}
                              </div>
                              <div className="text-neutral-700 mb-1">{prop.reasoning}</div>
                              <div className="text-neutral-600">
                                <span className="font-medium">Source:</span> {prop.sourceColumn}
                              </div>
                            </div>

                            {/* Accept button */}
                            <div className="ml-auto">
                              <button
                                onClick={() => handleAcceptProperty(prop)}
                                disabled={isAccepting || isAccepted}
                                className={`
                                  px-2 py-1 text-xs rounded flex items-center gap-1
                                  ${isAccepted
                                    ? 'bg-purple-100 text-purple-700 cursor-not-allowed'
                                    : 'bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50'
                                  }
                                `}
                              >
                                {isAccepting ? (
                                  <>
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                    Accepting...
                                  </>
                                ) : isAccepted ? (
                                  <>
                                    <Check className="w-3 h-3" />
                                    Accepted
                                  </>
                                ) : (
                                  <>
                                    <Plus className="w-3 h-3" />
                                    Accept
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Column Mappings */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-neutral-900">Column Mappings</h4>
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                {showAdvanced ? 'Hide' : 'Show'} Advanced Details
              </button>
            </div>

            <div className="space-y-2">
              {editedMappings.map((column, idx) => (
                <div
                  key={idx}
                  className={`p-3 border rounded-lg ${
                    column.include ? 'bg-white border-neutral-200' : 'bg-neutral-50 border-neutral-200 opacity-60'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Include checkbox */}
                    <div className="flex items-center pt-1">
                      <input
                        type="checkbox"
                        checked={column.include}
                        onChange={() => handleToggleColumn(idx)}
                        className="rounded border-neutral-300 text-blue-600"
                      />
                    </div>

                    {/* Column info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-neutral-900">{column.columnName}</span>
                        <span className="text-xs text-neutral-500">({column.dataType})</span>
                        {column.suggestedMapping && (
                          <span className={`text-xs px-2 py-0.5 rounded ${getConfidenceColor(column.suggestedMapping.confidence)}`}>
                            {Math.round(column.suggestedMapping.confidence * 100)}%
                          </span>
                        )}
                      </div>

                      {/* Mapping selection */}
                      {column.include && (
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <div>
                            <label className="text-xs text-neutral-600 mb-1 block">Map to Node</label>
                            <select
                              value={column.suggestedMapping?.nodeLabel || ''}
                              onChange={(e) => handleMappingChange(idx, e.target.value, column.suggestedMapping?.propertyName || '', true)}
                              className="w-full px-2 py-1 text-sm border border-neutral-300 rounded"
                            >
                              <option value="">Select node...</option>
                              {schema.nodes.map(node => (
                                <option key={node.label} value={node.label}>{node.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-neutral-600 mb-1 block">Property Name</label>
                            <input
                              type="text"
                              list={`properties-${idx}`}
                              value={column.suggestedMapping?.propertyName || ''}
                              onChange={(e) => handleMappingChange(idx, column.suggestedMapping?.nodeLabel || '', e.target.value, false)}
                              onBlur={(e) => handleMappingChange(idx, column.suggestedMapping?.nodeLabel || '', e.target.value, true)}
                              placeholder="Type or select property"
                              className="w-full px-2 py-1 text-sm border border-neutral-300 rounded"
                            />
                            {column.suggestedMapping?.nodeLabel && (
                              <datalist id={`properties-${idx}`}>
                                {getNodeProperties(column.suggestedMapping.nodeLabel).map((prop, propIdx) => (
                                  <option key={propIdx} value={prop} />
                                ))}
                              </datalist>
                            )}
                          </div>
                        </div>
                      )}

                      {/* AI reasoning */}
                      {column.suggestedMapping && (
                        <div className="text-xs text-neutral-600 mb-2 p-2 bg-blue-50 rounded">
                          <span className="font-medium">Analysis:</span> {column.suggestedMapping.reasoning}
                        </div>
                      )}

                      {/* Advanced details */}
                      {showAdvanced && (
                        <div className="mt-2 pt-2 border-t border-neutral-200">
                          <div className="grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <span className="text-neutral-600">Null %:</span>
                              <span className={`ml-1 font-medium ${column.dataQuality.nullPercentage > 50 ? 'text-red-600' : 'text-green-600'}`}>
                                {column.dataQuality.nullPercentage.toFixed(1)}%
                              </span>
                            </div>
                            <div>
                              <span className="text-neutral-600">Uniqueness:</span>
                              <span className="ml-1 font-medium">{column.dataQuality.uniqueness.toFixed(1)}%</span>
                            </div>
                            <div>
                              <span className="text-neutral-600">Samples:</span>
                              <span className="ml-1 text-neutral-500">{column.sampleValues.slice(0, 3).join(', ')}</span>
                            </div>
                          </div>
                          {column.dataQuality.issues && column.dataQuality.issues.length > 0 && (
                            <div className="mt-2 text-xs text-amber-700">
                              Issues: {column.dataQuality.issues.join(', ')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Status icon */}
                    <div className="pt-1">
                      {column.include && column.suggestedMapping ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : column.include ? (
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-neutral-400" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardBody>
      )}
    </Card>
  );
};
